/*
 * CartRules - pure business rules of the cart drawer (phase 04).
 *
 * Unit: every amount is an integer in grosze (1999 = 19,99 zl).
 * These functions only CLASSIFY the cart and PREVIEW offers. Payable totals
 * always come from Shopify (/cart.js, section rendering) - nothing here is
 * ever sent to checkout as a price (CART-04).
 * No DOM, no network. Liquid (cart-drawer sections) mirrors the same rules;
 * scripts/verify-phase4.mjs checks the parity.
 *
 * defaults()                       -> cfg (thresholds, product types, tier rates, pack price).
 * lineAfterDiscount(price, pct)    -> price minus the percentage discount floored to whole
 *                                     grosze (how Shopify rounds a line discount).
 * stateFromCart(cart, cfg, extra)  -> normalised state: paid (total minus gift lines),
 *                                     physical / e-book / gift lines, physicalQty,
 *                                     digitalOnly, codeApplied (an applicable order code,
 *                                     D-21) and the extra flags passed in.
 * barState(state, cfg)             -> { key, amount } for the progress bar (199 / 300 zl,
 *                                     e-book-only variant without shipping, D-08/D-10).
 * tierNext(physicalQty, cfg)       -> { count, percent } of the next quantity tier or null.
 * ebookOffer(state, price, cfg)    -> { kind, total, delta } preview for adding one e-book
 *                                     (default / two / three / pack) or null.
 * shipGapOffer(state, offer, cfg)  -> true when that e-book closes the 199 zl gap (D-19).
 * shipNearGap(state, cfg)          -> true when the cart is just under free shipping (gap
 *                                     <= cfg.shipNear, 20 zl) and the e-book candidate
 *                                     closes the gap (owner decision 2026-10-08).
 * pickProposal(state, cfg)         -> 'gift' | 'filters' | 'tier' | 'ebook' | 'none' (D-16/D-17);
 *                                     shipNearGap makes the e-book win over the tier.
 * systemActions(state, cfg)        -> removals / trims the theme performs on its own
 *                                     (D-06, D-22, D-23). Never adds or increases.
 * missingItems(items, cart, cfg)   -> request items without e-books already in the cart
 *                                     (BNDL-01, D-22).
 */
(function (root) {
  'use strict';

  function defaults() {
    return {
      ship: 19900,
      shipNear: 2000,
      gift: 30000,
      giftType: 'Prezent',
      ebookType: 'E-Book',
      tiers: { 2: 10 },
      ebookRates: { 2: 10, 3: 20 },
      packPrice: 4999,
      ebookTotal: 4,
      tierExcludedType: 'Filtry do fontanny'
    };
  }

  function lineAfterDiscount(price, percent) {
    return price - Math.floor((price * percent) / 100);
  }

  function num(value) {
    var n = Number(value);
    return isFinite(n) ? n : 0;
  }

  function stateFromCart(cart, cfg, extra) {
    cart = cart || {};
    extra = extra || {};
    var items = cart.items || [];
    var giftMap = extra.giftMap || {};
    var physicalLines = [];
    var ebookLines = [];
    var giftLines = [];
    var giftTotal = 0;
    var physicalQty = 0;
    var tierQty = 0;
    var itemCount = 0;

    items.forEach(function (item) {
      var qty = num(item.quantity);
      itemCount += qty;
      if (item.product_type === cfg.giftType) {
        giftTotal += num(item.final_line_price);
        var original = giftMap[item.product_id];
        giftLines.push({
          key: item.key,
          productId: item.product_id,
          variantId: item.variant_id,
          originalProductId: original != null ? original : null,
          quantity: qty
        });
        return;
      }
      if (item.product_type === cfg.ebookType) {
        ebookLines.push({
          key: item.key,
          productId: item.product_id,
          variantId: item.variant_id,
          quantity: qty,
          originalLinePrice: num(item.original_line_price),
          finalLinePrice: num(item.final_line_price)
        });
      }
      if (item.requires_shipping) {
        physicalQty += qty;
        // Fountain filters are outside the quantity discount collection.
        if (item.product_type !== cfg.tierExcludedType) tierQty += qty;
        physicalLines.push({
          key: item.key,
          productId: item.product_id,
          variantId: item.variant_id,
          quantity: qty
        });
      }
    });

    if (cart.item_count != null) itemCount = num(cart.item_count);

    var codeApplied = (cart.discount_codes || []).some(function (code) {
      return code && code.applicable === true;
    });

    return {
      itemCount: itemCount,
      paid: num(cart.total_price) - giftTotal,
      codeApplied: codeApplied,
      physicalQty: physicalQty,
      tierQty: tierQty,
      digitalOnly: itemCount > 0 && physicalQty === 0,
      physicalLines: physicalLines,
      ebookLines: ebookLines,
      giftLines: giftLines,
      giftAvailable: !!extra.giftAvailable,
      tierTargetAvailable: !!extra.tierTargetAvailable,
      ebookCandidate: extra.ebookCandidate || null,
      filterOffer: extra.filterOffer || null
    };
  }

  function barState(state, cfg) {
    if (!state.itemCount) return { key: 'empty', amount: 0 };
    var hasGift = state.giftLines.length > 0;
    var prefix = state.digitalOnly ? 'digital_' : 'gift_';
    if (!state.digitalOnly && state.paid < cfg.ship) {
      return { key: 'ship_missing', amount: cfg.ship - state.paid };
    }
    if (state.paid < cfg.gift) {
      return { key: prefix + 'missing', amount: cfg.gift - state.paid };
    }
    if (hasGift) return { key: prefix + 'chosen', amount: 0 };
    if (state.giftAvailable) return { key: prefix + 'choose', amount: 0 };
    return { key: state.digitalOnly ? 'digital_done' : 'ship_done', amount: 0 };
  }

  function tierNext(physicalQty, cfg) {
    var next = physicalQty + 1;
    if (physicalQty < 1 || !cfg.tiers[next]) return null;
    return { count: next, percent: cfg.tiers[next] };
  }

  function ebookOffer(state, candidatePrice, cfg) {
    var lines = state.ebookLines;
    if (lines.length >= cfg.ebookTotal) return null;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].quantity > 1) return null;
    }
    if (state.codeApplied || lines.length === 0) {
      return { kind: 'default', total: candidatePrice, delta: candidatePrice };
    }
    var current = 0;
    lines.forEach(function (line) { current += line.finalLinePrice; });
    var next = lines.length + 1;
    var total;
    var kind;
    if (next === cfg.ebookTotal) {
      kind = 'pack';
      total = cfg.packPrice;
    } else {
      var percent = cfg.ebookRates[next];
      if (!percent) return null;
      kind = next === 2 ? 'two' : 'three';
      total = lineAfterDiscount(candidatePrice, percent);
      lines.forEach(function (line) {
        total += lineAfterDiscount(line.originalLinePrice, percent);
      });
    }
    return { kind: kind, total: total, delta: total - current };
  }

  function shipGapOffer(state, offer, cfg) {
    if (state.codeApplied || !offer) return false;
    return state.physicalQty > 0 && state.paid < cfg.ship && state.paid + offer.delta >= cfg.ship;
  }

  // Owner decision 2026-10-08: just under the free-shipping threshold the
  // "Dobierz e-book ..., a dostawe masz gratis" box beats the quantity tier.
  function shipNearGap(state, cfg) {
    var candidate = state.ebookCandidate;
    if (!candidate || state.codeApplied) return false;
    var gap = cfg.ship - state.paid;
    if (gap <= 0 || gap > cfg.shipNear) return false;
    return shipGapOffer(state, ebookOffer(state, num(candidate.price), cfg), cfg);
  }

  function pickProposal(state, cfg) {
    if (!state.itemCount) return 'none';
    if (state.paid >= cfg.gift && state.giftLines.length === 0 && state.giftAvailable) return 'gift';
    if (shipNearGap(state, cfg)) return 'ebook';
    if (state.filterOffer) return 'filters';
    if (!state.codeApplied && state.tierTargetAvailable && tierNext(state.tierQty, cfg)) return 'tier';
    if (state.ebookCandidate) return 'ebook';
    return 'none';
  }

  function systemActions(state, cfg) {
    var actions = [];

    state.ebookLines.forEach(function (line) {
      if (line.quantity > 1) {
        actions.push({ type: 'trim-ebook', key: line.key, quantity: 1, notice: null, amount: 0 });
      }
    });

    var gifts = state.giftLines;
    if (gifts.length === 0) return actions;

    if (state.paid < cfg.gift) {
      gifts.forEach(function (gift) {
        actions.push({ type: 'remove-gift', key: gift.key, quantity: 0, notice: 'gift_removed', amount: cfg.gift - state.paid });
      });
      return actions;
    }

    var paidEbooks = {};
    state.ebookLines.forEach(function (line) { paidEbooks[String(line.productId)] = true; });

    var kept = false;
    gifts.forEach(function (gift) {
      if (gift.originalProductId != null && paidEbooks[String(gift.originalProductId)]) {
        actions.push({ type: 'remove-gift', key: gift.key, quantity: 0, notice: 'gift_duplicate', amount: 0 });
        return;
      }
      if (kept) {
        actions.push({ type: 'remove-gift', key: gift.key, quantity: 0, notice: null, amount: 0 });
        return;
      }
      kept = true;
      if (gift.quantity > 1) {
        actions.push({ type: 'trim-gift', key: gift.key, quantity: 1, notice: null, amount: 0 });
      }
    });

    return actions;
  }

  // Request items are de-duplicated by variant id (first occurrence wins);
  // variants already in the cart as digital lines (e-books) are skipped.
  function missingItems(items, cart, cfg) {
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (item) {
      if (item.requires_shipping === false || item.product_type === cfg.ebookType) {
        inCart[String(item.variant_id)] = true;
      }
    });
    var seen = {};
    var kept = [];
    var skipped = [];
    (items || []).forEach(function (item) {
      var id = String(item.id);
      if (inCart[id]) {
        skipped.push(item.id);
        return;
      }
      if (seen[id]) return;
      seen[id] = true;
      kept.push(item);
    });
    return { items: kept, skipped: skipped };
  }

  root.CartRules = {
    defaults: defaults,
    lineAfterDiscount: lineAfterDiscount,
    stateFromCart: stateFromCart,
    barState: barState,
    tierNext: tierNext,
    ebookOffer: ebookOffer,
    shipGapOffer: shipGapOffer,
    shipNearGap: shipNearGap,
    pickProposal: pickProposal,
    systemActions: systemActions,
    missingItems: missingItems
  };
})(typeof window !== 'undefined' ? window : globalThis);
